import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Clock, Loader2, Mail, MessageSquare, Search, User } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { BrandCard, BrandCardContent } from "@/components/brand/brand-card";
import { BrandInput } from "@/components/brand/brand-input";
import { BrandSelect } from "@/components/brand/brand-select";
import { Header } from "@/components/Header";
import { useSession } from "@/hooks/auth-hooks";

export const Route = createFileRoute("/admin/feedback")({
	component: AdminFeedbackPage,
});

type FeedbackItem = {
	id: string;
	category?: string;
	contactEmail?: string;
	createdAt: number;
	message: string;
	userEmail?: string;
	userName?: string;
	user?: Array<{ id: string; email?: string; name?: string }>;
};

type CategoryStyle = { color: string; bg: string; border: string };

const CATEGORY_STYLES: Record<string, CategoryStyle> = {
	bug: { color: "#6B3FF2", bg: "rgba(234,40,4,0.08)", border: "rgba(234,40,4,0.3)" },
	idea: { color: "#79c0ff", bg: "rgba(121,192,255,0.08)", border: "rgba(121,192,255,0.3)" },
	ux: { color: "#c0a0ff", bg: "rgba(192,160,255,0.08)", border: "rgba(192,160,255,0.3)" },
	instagram: { color: "#ff79c0", bg: "rgba(255,121,192,0.08)", border: "rgba(255,121,192,0.3)" },
	onboarding: { color: "#e6b800", bg: "rgba(230,184,0,0.08)", border: "rgba(230,184,0,0.3)" },
	other: { color: "#888888", bg: "rgba(173,170,170,0.08)", border: "rgba(173,170,170,0.3)" },
};

const CATEGORY_OPTIONS = [
	{ value: "all", label: "All categories" },
	{ value: "bug", label: "Bug" },
	{ value: "idea", label: "Idea" },
	{ value: "ux", label: "UX" },
	{ value: "instagram", label: "Instagram" },
	{ value: "onboarding", label: "Onboarding" },
	{ value: "other", label: "Other" },
];

function AdminFeedbackPage() {
	const { session, user, isPending } = useSession();
	const navigate = useNavigate();
	const [search, setSearch] = useState("");
	const [categoryFilter, setCategoryFilter] = useState<string>("all");

	const isAdmin = user?.role === "admin";

	useEffect(() => {
		if (isPending) return;
		if (!session || !user) {
			navigate({ to: "/login", replace: true });
			return;
		}
		if (!isAdmin) {
			navigate({ to: "/dashboard", replace: true });
		}
	}, [isPending, session, user, isAdmin, navigate]);

	const { data: feedbackItems = [], isLoading } = useQuery({
		queryKey: ["admin-feedback"],
		queryFn: async () => {
			const res = await fetch("/api/admin/list-feedback", {
				credentials: "include",
			});
			if (!res.ok) throw new Error("Failed to load feedback");
			const data = await res.json();
			return (data.feedback ?? []) as FeedbackItem[];
		},
		enabled: !isPending && !!session && !!user && isAdmin,
	});

	const sortedFeedback = useMemo(() => {
		return [...feedbackItems].sort((a, b) => b.createdAt - a.createdAt);
	}, [feedbackItems]);

	const filteredFeedback = useMemo(() => {
		let items = sortedFeedback;

		if (categoryFilter !== "all") {
			items = items.filter(
				(f) => (f.category ?? "other") === categoryFilter,
			);
		}

		const query = search.trim().toLowerCase();
		if (!query) return items;

		return items.filter((f) => {
			const message = f.message.toLowerCase();
			const email = (f.userEmail ?? f.contactEmail ?? "").toLowerCase();
			const name = (f.userName ?? "").toLowerCase();
			return (
				message.includes(query) ||
				email.includes(query) ||
				name.includes(query)
			);
		});
	}, [sortedFeedback, search, categoryFilter]);

	if (isPending || isLoading || !isAdmin) {
		return (
			<div className="min-h-svh flex items-center justify-center bg-background">
				<Loader2 className="w-8 h-8 animate-spin text-primary" />
			</div>
		);
	}

	return (
		<div className="min-h-svh bg-background">
			<Header />

			<main className="max-w-5xl mx-auto px-4 py-8">
				<div className="mb-8">
					<p
						className="text-[10px] font-bold uppercase tracking-[0.1em] mb-3"
						style={{ color: "#6B3FF2", fontFamily: "inherit" }}
					>
						Admin
					</p>
					<h1
						className="text-3xl font-semibold tracking-tight"
						style={{ fontFamily: "'Geist', -apple-system, sans-serif", color: "var(--km-on-surface)" }}
					>
						Feedback
					</h1>
					<p className="mt-1 text-sm" style={{ color: "var(--km-on-surface-muted)" }}>
						{feedbackItems.length} submission{feedbackItems.length !== 1 ? "s" : ""} from MCP agents
					</p>
				</div>

				<div className="flex flex-col sm:flex-row gap-3 mb-6">
					<BrandInput
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						placeholder="Search feedback..."
						icon={<Search className="w-4 h-4" />}
						className="sm:max-w-xs"
					/>
					<BrandSelect
						value={categoryFilter}
						onChange={(e) => setCategoryFilter(e.target.value)}
						options={CATEGORY_OPTIONS}
						className="sm:w-48"
					/>
				</div>

				<div className="space-y-3">
					{filteredFeedback.map((f) => {
						const category = f.category ?? "other";
						const cs = CATEGORY_STYLES[category] ?? CATEGORY_STYLES.other;
						const displayEmail = f.userEmail ?? f.contactEmail ?? null;
						const displayName = f.userName ?? null;

						return (
							<BrandCard key={f.id}>
								<BrandCardContent className="p-5">
									<div className="flex items-start justify-between gap-4">
										<div className="flex-1 min-w-0">
											<div className="flex items-center gap-2.5 mb-3">
												<span
													className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em]"
													style={{
														color: cs.color,
														backgroundColor: cs.bg,
														border: `1px solid ${cs.border}`,
														fontFamily: "inherit",
													}}
												>
													{category}
												</span>
												<span
													className="inline-flex items-center gap-1 text-xs"
													style={{ color: "var(--km-on-surface-muted)", fontFamily: "'Geist Mono', 'JetBrains Mono', monospace" }}
												>
													<Clock className="w-3 h-3" />
													{new Date(f.createdAt).toLocaleDateString(undefined, {
														month: "short",
														day: "numeric",
														year: "numeric",
													})}
													{" "}
													{new Date(f.createdAt).toLocaleTimeString(undefined, {
														hour: "2-digit",
														minute: "2-digit",
													})}
												</span>
											</div>

											<p
												className="text-sm leading-relaxed whitespace-pre-wrap"
												style={{ color: "var(--km-on-surface)" }}
											>
												{f.message}
											</p>

											{(displayName || displayEmail) && (
												<div
													className="flex items-center gap-3 mt-3 pt-3"
													style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
												>
													{displayName && (
														<span
															className="inline-flex items-center gap-1.5 text-xs"
															style={{ color: "var(--km-on-surface-muted)" }}
														>
															<User className="w-3 h-3" />
															{displayName}
														</span>
													)}
													{displayEmail && (
														<span
															className="inline-flex items-center gap-1.5 text-xs"
															style={{ color: "var(--km-on-surface-muted)", fontFamily: "'Geist Mono', 'JetBrains Mono', monospace" }}
														>
															<Mail className="w-3 h-3" />
															{displayEmail}
														</span>
													)}
												</div>
											)}
										</div>
									</div>
								</BrandCardContent>
							</BrandCard>
						);
					})}

					{filteredFeedback.length === 0 && (
						<BrandCard>
							<BrandCardContent className="py-16">
								<div className="text-center">
									<MessageSquare className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
									<p className="text-sm text-muted-foreground">
										{search || categoryFilter !== "all"
											? "No feedback matches your filters"
											: "No feedback received yet"}
									</p>
								</div>
							</BrandCardContent>
						</BrandCard>
					)}
				</div>
			</main>
		</div>
	);
}
